import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AssetSortMode } from '../utils/analysisGrouping';

interface AssetSortMenuProps {
  value: AssetSortMode;
  onChange: (mode: AssetSortMode) => void;
}

const MENU_WIDTH = 160;

/** 资产分析组间排序方式下拉，样式对齐"筛选剧集"控件。 */
export default function AssetSortMenu({ value, onChange }: AssetSortMenuProps) {
  const { t } = useTranslation('projectDetail');
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const options: Array<{ key: AssetSortMode; label: string }> = [
    { key: 'episodeCount', label: t('analysis.sortByEpisodeCount') },
    { key: 'appearance', label: t('analysis.sortByAppearance') },
  ];

  const recomputePos = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - MENU_WIDTH) });
  }, []);

  const toggleOpen = () => {
    if (!open) recomputePos();
    setOpen(!open);
  };

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => recomputePos();
    document.addEventListener('mousedown', onDocClick);
    // 捕获阶段监听，任意可滚动祖先滚动时都能重新定位（对齐剧集筛选弹层的行为）
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, recomputePos]);

  const currentLabel = options.find(option => option.key === value)?.label || options[0].label;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
        {t('analysis.sortMode')}
      </span>
      <button
        type="button"
        ref={triggerRef}
        onClick={toggleOpen}
        style={{
          fontSize: '0.78rem',
          padding: '2px 8px',
          borderRadius: '4px',
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          color: 'var(--text)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {currentLabel}
        <span style={{ fontSize: '0.6rem' }}>▼</span>
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: 1000,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              padding: '0.3rem',
              width: `${MENU_WIDTH}px`,
            }}
          >
            {options.map(option => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text)',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  textAlign: 'left',
                }}
              >
                {option.label}
                {option.key === value && <span>✓</span>}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
